/**
 * Report result cache — in memory, per tab, never persisted.
 *
 * Reports cost real time: ~3s for a day, ~7-8s for a week, ~15s+ for a month,
 * and worse during the morning reporting peak. Following a resident link out of
 * a report and coming back used to mean paying that again, so this holds the
 * last few results and hands them straight back.
 *
 * See docs/REPORT_STATE_RESTORE_PLAN.md.
 *
 * WHY MEMORY AND NOT STORAGE. A detail row carries resident_name, area_name
 * (which is the room, so it identifies the resident), reason_code_description
 * (Fall, Medical Emergency) and free-text caregiver notes — up to 5,000 of them.
 * services/session.js already AES-GCM encrypts the mere session blob at rest;
 * putting report rows in plaintext sessionStorage would regress a standard this
 * codebase has already set. Memory also gets the lifetime right for free:
 * components/Logout.jsx ends with window.location.href = "/", which tears down
 * the JS context, so nothing survives for the next user of a shared nursing
 * station. clearReportCache() is wired anyway in case a soft logout ever lands.
 *
 * STALE-WHILE-REVALIDATE. An edit made on another page (a note added on the
 * event profile you just drilled into) must show when you come back. Plain
 * invalidation would force a full re-run and throw away the speed win exactly
 * when the user had drilled in to edit — the commonest case. So a mutation
 * marks the affected entry stale, the entry still hydrates instantly, and the
 * caller kicks a background refresh. No hand-modelled patching, so a restored
 * row cannot diverge from what a fresh query would return.
 */

/* Bounded on purpose: one entry is a few MB at the 5,000-row cap, and without a
   limit this grows with every filter permutation the user tries.
   Least-recently-written is evicted.

   Was 3, when Alert Details was the only consumer. Five report types now share
   this cache, so 3 could not hold even one run per report -- opening Shift
   after Alerts evicted Alerts, and the nav-menu offer correctly but
   unhelpfully vanished. 6 holds one per report plus a spare permutation. The
   worst case is only reached if every cached report is simultaneously at the
   5,000-row cap, which is not a real usage pattern. */
const MAX_ENTRIES = 6;

/** key -> { config, records, summary, overLimit, csvUrl, extra, ranAt, stale, eventIds } */
const entries = new Map();

/** `${reportType}|${campusId}` -> key. Backs the Phase 3 nav-menu offer. */
const lastRunByScope = new Map();

const scopeOf = (reportType, campusId) => `${reportType}|${campusId}`;

/**
 * Stable key for a report run.
 *
 * Canonicalised, because two runs of the same report must produce the same
 * string: object key order is not guaranteed across code paths, and the filter
 * arrays arrive in whatever order the drawer built them. Without the sort, a
 * restored config could miss its own cache entry.
 *
 * campusId is part of the key, not just the scope: handleCampusChange already
 * clears the report, and an entry that ignored campus would hand another
 * campus's residents straight back.
 */
export function makeReportKey(reportType, campusId, config) {
    const canon = (v) => {
        if (Array.isArray(v)) return [...v].map(canon).sort();
        if (v && typeof v === 'object') {
            return Object.keys(v).sort().reduce((acc, k) => {
                acc[k] = canon(v[k]);
                return acc;
            }, {});
        }
        if (v instanceof Date) return v.toISOString();
        return v;
    };
    return JSON.stringify({ reportType, campusId: String(campusId), config: canon(config) });
}

/** The cached result for a key, or null. Does not clear the stale flag. */
export function readReport(key) {
    if (!key) return null;
    return entries.get(key) || null;
}

/**
 * Store a result.
 *
 * `eventIds` is what makes targeted staleness possible — see markEventsChanged.
 * overLimit and csvUrl are stored because the >5,000-event view branches on
 * them; without them a restored over-limit report would render as a normal one
 * and quietly lie about being complete.
 *
 * `extra` is an opaque bag for whatever else a report renders from. Every page
 * past the first turned out to have some: Shift has hours + boundaries,
 * Caregiver has totals, Hourly has its stats block. Restoring records alone
 * would put those pages on screen with their tables full and their headline
 * figures blank, which is worse than not restoring at all. Opaque on purpose --
 * the cache has no business knowing what a shift boundary is.
 */
export function writeReport(key, { config, records, summary, overLimit, csvUrl, extra }, scope) {
    if (!key) return;
    const eventIds = new Set(
        (records || [])
            .map((r) => r?.event_id)
            .filter((v) => v !== null && v !== undefined)
            .map(String),
    );
    if (entries.size >= MAX_ENTRIES && !entries.has(key)) {
        const oldest = entries.keys().next().value;
        if (oldest !== undefined) entries.delete(oldest);
    }
    entries.delete(key);                    // re-insert so Map order is LRW
    entries.set(key, {
        /* Stored verbatim, not re-derived from the key. The key holds a
           CANONICALISED copy (arrays sorted) for stable hashing; restoring from
           that would quietly reorder the user's filter selections. */
        config,
        records,
        summary,
        overLimit: Boolean(overLimit),
        csvUrl: csvUrl || '',
        extra: extra || null,
        ranAt: Date.now(),
        stale: false,
        eventIds,
        /* An AGGREGATE report -- Shift, Caregiver -- has one row per shift or
           per caregiver, with no event_id anywhere on it. Such an entry cannot
           prove an edit missed it, and a reason-code change really does move
           its numbers, so it goes stale on ANY event change rather than none.
           Same lopsided trade as marking on attempt: a false positive costs one
           background refresh, a miss shows figures that contradict an edit the
           user just made. */
        blanketInvalidate: eventIds.size === 0,
    });
    if (scope) lastRunByScope.set(scopeOf(scope.reportType, scope.campusId), key);
}

/**
 * Mark every entry that contains one of these event ids as stale.
 *
 * Scoped by id rather than blanket, so a note added to an event that is not in
 * the cached report costs nothing -- except for aggregate reports, which carry
 * no event ids and so are always marked (see blanketInvalidate). Returns how
 * many entries were affected, which is only useful for tests and logging.
 */
export function markEventsChanged(eventIds) {
    const ids = (Array.isArray(eventIds) ? eventIds : [eventIds])
        .filter((v) => v !== null && v !== undefined)
        .map(String);
    if (!ids.length) return 0;
    let hit = 0;
    entries.forEach((entry) => {
        if (entry.blanketInvalidate || ids.some((id) => entry.eventIds.has(id))) {
            entry.stale = true;
            hit += 1;
        }
    });
    return hit;
}

/** The key of the last run for a scope, or null. */
export function readLastRunKey(reportType, campusId) {
    return lastRunByScope.get(scopeOf(reportType, campusId)) || null;
}

/**
 * The last run for a scope as { key, config, ranAt, stale }, or null.
 *
 * Backs the nav-menu offer: arriving via the nav is a fresh intent, so the page
 * does not restore behind the user's back — it offers, and needs the config and
 * the timestamp to describe what it is offering. Returns null when the pointer
 * survives but the entry has been evicted, so a stale pointer cannot produce an
 * offer that then fails to restore.
 */
export function readLastRun(reportType, campusId) {
    const key = readLastRunKey(reportType, campusId);
    if (!key) return null;
    const entry = entries.get(key);
    if (!entry) return null;
    /* No config means nothing to replay, so there is nothing to offer. Without
       this guard a caller that forgot to store the config still gets a truthy
       offer, the banner renders, and the button silently does nothing -- which
       is exactly how this shipped the first time. Fail as "no offer", never as
       a dead control. */
    if (!entry.config) return null;
    return {
        key,
        config: entry.config,
        ranAt: entry.ranAt,
        stale: entry.stale,
        /* Surfaced in the offer. An offer that names its event count is
           self-diagnosing: the user can tell at a glance whether it is the
           report they are thinking of, instead of accepting it and then
           wondering why the numbers changed. */
        count: (entry.records || []).length,
    };
}

/** Belt-and-braces for a soft logout; a hard one tears the context down anyway. */
export function clearReportCache() {
    entries.clear();
    lastRunByScope.clear();
}

/** Test/debug visibility. Not for product code. */
export function _cacheStats() {
    return {
        size: entries.size,
        keys: [...entries.keys()],
        stale: [...entries.values()].filter((e) => e.stale).length,
    };
}
